import React from "react";
import GoalGrid from "@/components/GoalGrid";

/**
 * Extended goal visualization with an optional outer "miss frame" that
 * surrounds the standard 3x3 goal grid.
 *
 * The frame has 6 zones (placements 10-15):
 *   10 – ovanför ribban, vänster
 *   11 – ovanför ribban, höger
 *   12 – utanför vänsterstolpe, högt
 *   13 – utanför vänsterstolpe, lågt
 *   14 – utanför högerstolpe, högt
 *   15 – utanför högerstolpe, lågt
 *
 * Diagonaler skär från målets överkryss ut till ramens övre hörn.
 *
 * Props:
 *   placement, onSelect, stats, selectable, showEmpty, testIdPrefix, testId
 *     — same as GoalGrid, passed through untouched.
 *   showMissFrame:      render the outer frame at all (default true)
 *   missSelectable:     make outer frame zones clickable
 *   missStats:          { "10": {total}, "11": {total}, ... "15": {total} }
 *   onMissSelect:       (placement:10..15) => void
 */

// SVG-space – outer frame 90x75, goal rect inside is square (15,15)-(75,75)
const V = { W: 90, H: 75, gx1: 15, gy1: 15, gx2: 75, gy2: 75, midX: 45, midY: 45 };

const MISS_ZONES = [
  { id: 10, label: "10", d: `M0,0 L45,0 L45,15 L15,15 Z`, labelPos: { x: 22.5, y: 7 } },
  { id: 11, label: "11", d: `M45,0 L90,0 L75,15 L45,15 Z`, labelPos: { x: 67.5, y: 7 } },
  {
    id: 12,
    label: "12",
    d: `M0,0 L15,15 L15,45 L0,45 Z`,
    labelPos: { x: 7.5, y: 30 },
  },
  {
    id: 13,
    label: "13",
    d: `M0,45 L15,45 L15,75 L0,75 Z`,
    labelPos: { x: 7.5, y: 60 },
  },
  {
    id: 14,
    label: "14",
    d: `M75,15 L90,0 L90,45 L75,45 Z`,
    labelPos: { x: 82.5, y: 30 },
  },
  {
    id: 15,
    label: "15",
    d: `M75,45 L90,45 L90,75 L75,75 Z`,
    labelPos: { x: 82.5, y: 60 },
  },
];

export default function GoalFrame({
  placement,
  onSelect,
  stats,
  selectable = false,
  showEmpty = false,
  testIdPrefix = "goal-cell",
  testId = "goal-grid",
  showMissFrame = true,
  missSelectable = false,
  missStats,
  onMissSelect,
  missTestIdPrefix = "miss-cell",
}) {
  if (!showMissFrame) {
    return (
      <GoalGrid
        placement={placement}
        onSelect={onSelect}
        stats={stats}
        selectable={selectable}
        showEmpty={showEmpty}
        testIdPrefix={testIdPrefix}
        testId={testId}
      />
    );
  }

  // Inner goal rect covers 75% wide × 80% tall of container (matches SVG coords)
  const innerStyle = {
    position: "absolute",
    left: `${(V.gx1 / V.W) * 100}%`,
    top: `${(V.gy1 / V.H) * 100}%`,
    width: `${((V.gx2 - V.gx1) / V.W) * 100}%`,
    height: `${((V.gy2 - V.gy1) / V.H) * 100}%`,
  };

  return (
    <div
      className="relative w-full"
      style={{ aspectRatio: `${V.W} / ${V.H}` }}
      data-testid={`${testId}-frame`}
    >
      <svg
        viewBox={`0 0 ${V.W} ${V.H}`}
        preserveAspectRatio="none"
        className="absolute inset-0 w-full h-full block"
      >
        {MISS_ZONES.map((z) => {
          const zStats = missStats?.[String(z.id)];
          const total = zStats?.total ?? 0;
          const isSelected = missSelectable && placement === z.id;
          const isActive = missSelectable && !isSelected;
          const heat = !missSelectable && total > 0;
          const fill = isSelected
            ? "#FF3B30"
            : heat
            ? "rgba(255, 59, 48, 0.35)"
            : isActive
            ? "rgba(255, 255, 255, 0.03)"
            : "#0A0A0A";
          return (
            <g key={z.id}>
              <path
                d={z.d}
                fill={fill}
                stroke="#ffffff"
                strokeWidth="0.6"
                style={{
                  cursor: missSelectable ? "pointer" : "default",
                  transition: "fill 160ms ease",
                }}
                onClick={() =>
                  missSelectable && onMissSelect && onMissSelect(z.id)
                }
                onMouseEnter={(e) => {
                  if (!missSelectable || isSelected) return;
                  e.currentTarget.setAttribute(
                    "fill",
                    "rgba(255, 59, 48, 0.35)"
                  );
                }}
                onMouseLeave={(e) => {
                  if (!missSelectable || isSelected) return;
                  e.currentTarget.setAttribute("fill", "rgba(255,255,255,0.03)");
                }}
                data-testid={`${missTestIdPrefix}-${z.id}`}
                data-selected={isSelected || undefined}
              />
              {!missSelectable && (
                <text
                  x={z.labelPos.x}
                  y={z.labelPos.y + (total > 0 ? -1 : 1)}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill={total > 0 ? "#ffffff" : "#3F3F46"}
                  fontFamily="Barlow Condensed, Arial, sans-serif"
                  fontWeight={total > 0 ? "900" : "500"}
                  fontSize={total > 0 ? "6" : "3.6"}
                  letterSpacing="0.3"
                  pointerEvents="none"
                >
                  {total > 0 ? total : z.label}
                </text>
              )}
              {missSelectable && (
                <text
                  x={z.labelPos.x}
                  y={z.labelPos.y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill={isSelected ? "#ffffff" : "#71717A"}
                  fontFamily="Barlow Condensed, Arial, sans-serif"
                  fontWeight="900"
                  fontSize="5"
                  letterSpacing="0.4"
                  pointerEvents="none"
                >
                  {z.label}
                </text>
              )}
            </g>
          );
        })}

        {/* Goal frame border (kryss visible via the corners of this rect) */}
        <rect
          x={V.gx1}
          y={V.gy1}
          width={V.gx2 - V.gx1}
          height={V.gy2 - V.gy1}
          fill="none"
          stroke="#ffffff"
          strokeWidth="0.9"
        />
      </svg>

      {/* Inner 3x3 grid overlays the goal rect */}
      <div style={innerStyle}>
        <GoalGrid
          placement={placement}
          onSelect={onSelect}
          stats={stats}
          selectable={selectable}
          showEmpty={showEmpty}
          testIdPrefix={testIdPrefix}
          testId={testId}
          hideFrame
        />
      </div>
    </div>
  );
}
